import { runAfterLoad } from './gsap-lazy';

function init() {
  const earthContainer = document.getElementById('canvas-container');
  const heroSection = document.getElementById('Startseite');
  const earthMediaQuery = window.matchMedia('(min-width: 768px)');
  const parallaxLayers = Array.from(document.querySelectorAll('.parallax-layer'));

  const EARTH_ACTIVE_WINDOW_MS = 4500;
  const EARTH_INTRO_WINDOW_MS = 6500;
  const EARTH_TARGET_FPS = 60;

  const earthState = {
    modulesPromise: null,
    scene: null,
    camera: null,
    renderer: null,
    ktx2Loader: null,
    mixer: null,
    model: null,
    clock: null,
    isMounted: false,
    isMounting: false,
    isLoopRunning: false,
    isPageVisible: !document.hidden,
    isHeroVisible: true,
    isActivated: false,
    interactiveUntil: 0,
    rafId: 0,
    lastFrameTime: 0,
    mountId: 0,
  };

  const parallaxState = {
    rafId: 0,
  };

  function setEarthPhase(phase) {
    if (!earthContainer) return;
    earthContainer.classList.toggle('earth-idle', phase === 'idle');
    earthContainer.classList.toggle('earth-loading', phase === 'loading');
    earthContainer.classList.toggle('earth-ready', phase === 'ready');
  }

  if (heroSection) {
    const rect = heroSection.getBoundingClientRect();
    earthState.isHeroVisible = rect.bottom > 0 && rect.top < window.innerHeight;
  }

  if (earthContainer) {
    setEarthPhase('idle');
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function isDesktop() {
    return window.innerWidth >= 1024;
  }

  function applyParallax() {
    if (!heroSection || parallaxLayers.length === 0) return;

    const rect = heroSection.getBoundingClientRect();
    const progress = clamp(-rect.top / Math.max(rect.height, 1), 0, 1);

    parallaxLayers.forEach((layer) => {
      const depth = parseFloat(layer.dataset.depth) || 0;
      layer.style.setProperty('--y', `${progress * depth * 800}px`);
    });
  }

  function requestParallaxUpdate() {
    if (parallaxState.rafId) return;

    parallaxState.rafId = requestAnimationFrame(() => {
      parallaxState.rafId = 0;
      applyParallax();
    });
  }

  async function loadThreeModules() {
    if (!earthState.modulesPromise) {
      earthState.modulesPromise = Promise.all([
        import('three'),
        import('three/examples/jsm/loaders/GLTFLoader.js'),
        import('three/examples/jsm/loaders/KTX2Loader.js'),
        import('three/examples/jsm/libs/meshopt_decoder.module.js'),
      ]).then(([THREE, { GLTFLoader }, { KTX2Loader }, { MeshoptDecoder }]) => ({
        THREE,
        GLTFLoader,
        KTX2Loader,
        MeshoptDecoder,
      }));
    }
    return earthState.modulesPromise;
  }

  function disposeMaterial(material) {
    if (!material) return;
    const materials = Array.isArray(material) ? material : [material];
    materials.forEach((mat) => {
      Object.values(mat).forEach((value) => {
        if (value && value.isTexture && typeof value.dispose === 'function') {
          value.dispose();
        }
      });
      mat.dispose();
    });
  }

  function disposeObject3D(object3D) {
    if (!object3D) return;
    object3D.traverse((child) => {
      if (!child.isMesh) return;
      if (child.geometry && typeof child.geometry.dispose === 'function') {
        child.geometry.dispose();
      }
      disposeMaterial(child.material);
    });
  }

  function renderEarth(delta = 0) {
    if (!earthState.renderer || !earthState.scene || !earthState.camera) return;

    if (delta > 0) {
      if (earthState.mixer) earthState.mixer.update(delta);
      if (earthState.model) earthState.model.rotation.y += delta * 0.25;
    }

    earthState.renderer.render(earthState.scene, earthState.camera);
  }

  function resizeEarth() {
    if (!earthState.isMounted || !earthState.renderer || !earthState.camera) return;

    let width;
    let height;

    if (isDesktop()) {
      width = window.innerWidth / 3;
      height = (3 * window.innerHeight) / 4;
      if (earthState.model) earthState.model.scale.set(2.5, 2.5, 2.5);
    } else {
      width = window.innerWidth;
      height = (3 * window.innerHeight) / 8;
      if (earthState.model) earthState.model.scale.set(3, 3, 3);
    }

    earthState.renderer.setSize(width, height);
    earthState.camera.aspect = width / height;
    earthState.camera.updateProjectionMatrix();
    renderEarth();
  }

  function markEarthActive(duration = EARTH_ACTIVE_WINDOW_MS) {
    const now = performance.now();
    earthState.interactiveUntil = Math.max(earthState.interactiveUntil, now + duration);
    updateEarthLoopState();
  }

  function canRenderEarth(now = performance.now()) {
    return !!(
      earthState.isMounted &&
      earthState.renderer &&
      earthMediaQuery.matches &&
      earthState.isPageVisible &&
      earthState.isHeroVisible &&
      now < earthState.interactiveUntil
    );
  }

  function earthAnimate(now) {
    if (!earthState.isLoopRunning) return;

    if (!canRenderEarth(now)) {
      stopEarthLoop();
      renderEarth();
      return;
    }

    const frameDuration = 1000 / EARTH_TARGET_FPS;
    if (now - earthState.lastFrameTime >= frameDuration) {
      const delta = earthState.clock ? Math.min(earthState.clock.getDelta(), 0.08) : 0;
      renderEarth(delta);
      earthState.lastFrameTime = now;
    }

    earthState.rafId = requestAnimationFrame(earthAnimate);
  }

  function startEarthLoop() {
    if (!earthState.renderer || earthState.isLoopRunning) return;
    earthState.isLoopRunning = true;
    earthState.lastFrameTime = 0;
    earthState.rafId = requestAnimationFrame(earthAnimate);
  }

  function stopEarthLoop() {
    if (!earthState.isLoopRunning) return;
    earthState.isLoopRunning = false;
    if (earthState.rafId) {
      cancelAnimationFrame(earthState.rafId);
      earthState.rafId = 0;
    }
  }

  function updateEarthLoopState() {
    if (canRenderEarth()) {
      startEarthLoop();
    } else {
      stopEarthLoop();
    }
  }

  async function mountEarth() {
    if (
      !earthContainer ||
      earthState.isMounted ||
      earthState.isMounting ||
      !earthMediaQuery.matches ||
      !earthState.isActivated
    ) {
      return;
    }

    earthState.isMounting = true;
    const mountId = ++earthState.mountId;
    setEarthPhase('loading');

    try {
      const { THREE, GLTFLoader, KTX2Loader, MeshoptDecoder } = await loadThreeModules();
      if (mountId !== earthState.mountId || !earthMediaQuery.matches || !earthContainer || !earthState.isActivated) return;

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 10000);
      camera.position.set(0, 7, 10);

      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.1));
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFShadowMap;
      renderer.setClearAlpha(0);
      renderer.domElement.classList.add('earth-canvas');

      const pointLight = new THREE.DirectionalLight(0xffffff, 4);
      pointLight.position.set(150, 150, 100);
      scene.add(pointLight);

      const ambientLight = new THREE.HemisphereLight(0xffffff, 0xffffff, 0.3);
      scene.add(ambientLight);

      earthState.scene = scene;
      earthState.camera = camera;
      earthState.renderer = renderer;
      earthState.clock = new THREE.Clock();
      earthState.mixer = null;
      earthState.model = null;
      earthState.isMounted = true;
      earthState.isLoopRunning = false;

      const earthURL = new URL('earth_cartoon.glb', import.meta.url);
      const earthLoader = new GLTFLoader();
      const transcoderPath = `${import.meta.env.BASE_URL}basis/`;
      const ktx2Loader = new KTX2Loader().setTranscoderPath(transcoderPath).detectSupport(renderer);
      earthState.ktx2Loader = ktx2Loader;
      earthLoader.setKTX2Loader(ktx2Loader);
      earthLoader.setMeshoptDecoder(MeshoptDecoder);
      earthLoader.load(
        earthURL.href,
        (gltf) => {
          if (mountId !== earthState.mountId || !earthState.scene) {
            disposeObject3D(gltf.scene);
            return;
          }

          earthState.model = gltf.scene;
          earthState.model.position.set(0, 7.5, 0);
          earthState.model.rotation.x = 0.1;
          earthState.model.rotation.y = -2.5;
          earthState.model.rotation.z = -0.2;
          earthState.scene.add(earthState.model);

          earthState.mixer = new THREE.AnimationMixer(earthState.model);
          gltf.animations.forEach((clip) => {
            earthState.mixer.clipAction(clip).play();
          });

          resizeEarth();
          renderEarth();
          setEarthPhase('ready');
          markEarthActive(EARTH_INTRO_WINDOW_MS);
        },
        undefined,
        (error) => {
          if (mountId !== earthState.mountId) return;
          console.error('Error loading earth model:', error);
          setEarthPhase('idle');
        }
      );

      if (!earthContainer.contains(renderer.domElement)) {
        earthContainer.appendChild(renderer.domElement);
      }

      resizeEarth();
      renderEarth();

      if (!earthMediaQuery.matches) {
        disposeEarth();
      }
    } catch (error) {
      if (mountId === earthState.mountId) {
        console.error('Error initializing earth scene:', error);
        setEarthPhase('idle');
      }
    } finally {
      if (mountId === earthState.mountId) {
        earthState.isMounting = false;
      }
    }
  }

  function disposeEarth() {
    if (!earthState.isMounted && !earthState.isMounting) return;

    earthState.mountId += 1;
    earthState.isMounting = false;
    earthState.isMounted = false;

    stopEarthLoop();

    if (earthState.mixer && earthState.model) {
      earthState.mixer.stopAllAction();
      earthState.mixer.uncacheRoot(earthState.model);
    }
    earthState.mixer = null;

    if (earthState.model && earthState.scene) {
      earthState.scene.remove(earthState.model);
    }
    if (earthState.model) {
      disposeObject3D(earthState.model);
    }
    earthState.model = null;

    if (earthState.renderer) {
      if (earthState.renderer.domElement.parentNode) {
        earthState.renderer.domElement.remove();
      }
      earthState.renderer.dispose();
      if (typeof earthState.renderer.forceContextLoss === 'function') {
        earthState.renderer.forceContextLoss();
      }
    }

    earthState.renderer = null;
    if (earthState.ktx2Loader) {
      earthState.ktx2Loader.dispose();
      earthState.ktx2Loader = null;
    }
    earthState.camera = null;
    earthState.scene = null;
    earthState.clock = null;

    setEarthPhase(earthState.isActivated ? 'loading' : 'idle');
  }

  function syncEarthState() {
    if (earthMediaQuery.matches && earthState.isActivated) {
      void mountEarth();
    } else {
      disposeEarth();
    }
  }

  function activateEarthFeature() {
    if (earthState.isActivated) return;
    earthState.isActivated = true;
    setEarthPhase('loading');
    markEarthActive(EARTH_INTRO_WINDOW_MS);
    syncEarthState();
  }

  function setupEarthActivation() {
    const activate = () => activateEarthFeature();
    const oncePassive = { once: true, passive: true };

    window.addEventListener('pointermove', activate, oncePassive);
    window.addEventListener('wheel', activate, oncePassive);
    window.addEventListener('scroll', activate, oncePassive);
    window.addEventListener('touchstart', activate, oncePassive);
    window.addEventListener('keydown', activate, { once: true });

    if (earthContainer) {
      earthContainer.addEventListener('click', activate, { once: true });
    }
  }

  let lastActivityMark = 0;
  function keepEarthAliveFromUserActivity() {
    if (!earthState.isActivated || !earthState.isMounted) return;

    const now = performance.now();
    if (now - lastActivityMark < 180) return;
    lastActivityMark = now;
    markEarthActive();
  }

  function onViewportResize() {
    resizeEarth();
    requestParallaxUpdate();
  }

  function onViewportChange() {
    syncEarthState();
    requestParallaxUpdate();
  }

  window.addEventListener('scroll', requestParallaxUpdate, { passive: true });
  window.addEventListener('resize', onViewportResize);
  if (typeof earthMediaQuery.addEventListener === 'function') {
    earthMediaQuery.addEventListener('change', onViewportChange);
  } else {
    earthMediaQuery.addListener(onViewportChange);
  }

  window.addEventListener('pointermove', keepEarthAliveFromUserActivity, { passive: true });
  window.addEventListener('wheel', keepEarthAliveFromUserActivity, { passive: true });
  window.addEventListener('scroll', keepEarthAliveFromUserActivity, { passive: true });
  window.addEventListener('touchstart', keepEarthAliveFromUserActivity, { passive: true });
  window.addEventListener('keydown', keepEarthAliveFromUserActivity);

  document.addEventListener('visibilitychange', () => {
    earthState.isPageVisible = !document.hidden;
    updateEarthLoopState();
  });

  if (heroSection && 'IntersectionObserver' in window) {
    const heroObserver = new IntersectionObserver(
      ([entry]) => {
        earthState.isHeroVisible = !!entry?.isIntersecting;
        updateEarthLoopState();
      },
      { threshold: 0.05 }
    );
    heroObserver.observe(heroSection);
  }

  setupEarthActivation();
  syncEarthState();
  requestParallaxUpdate();
}

runAfterLoad(init);
